import React, { useEffect, useState } from 'react';
import {
  Overlay,
  Modal,
  ModalInner,
  ModalHeader,
  Title,
  Close,
  ProfileCard,
  Avatar,
  ProfileInfo,
  Name,
  Meta,
  BluePill,
  QRBox,
  QRHint,
  ErrorText,
  MetaText,
  Steps,
  StepTitle,
  StepList,
  StepItem,
  Primary,
} from './StudentIdModal.styles';
import qrCodeUrl from '../../assets/qr-code.svg';
import { emailFromName, generateQRCodeSVG, toTitleCaseSingle } from '../../utils';
import useDeviceType from '../../hooks/useDevice';
import { useContext } from 'react';
import { UserContext } from '../../context/UserContextBase';
import { ensureUserIdCookie } from '../../utils/userIdCookie';

type Props = {
  open: boolean;
  onClose: () => void;
};

const StudentIdModal: React.FC<Props> = ({ open, onClose }) => {
  const device = useDeviceType();
  const isMobile = device === 'mobile';
  const { sessionData, refreshPortalSession } = useContext(UserContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [qrPayload, setQrPayload] = useState<string>('');
  const userId = document.cookie.split('; ').find(c => c.startsWith('userId='))?.split('=')[1] ?? '';

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        setQrPayload('');
        // Ensure user id cookie is set (value is read from cookie for display)
        ensureUserIdCookie();

        const res = await fetch('/api/issue-student-id', {
          method: 'GET',
          credentials: 'include'
        });
        const json = await res.json();
        if (!res.ok || !json?.success) {
          throw new Error(json?.message || 'Failed to issues student id');
        }
        const data = json?.data;
        const { offerUrl } = data || {};
        if (!cancelled) {
          setQrPayload(offerUrl);
          await refreshPortalSession();
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message || 'Unexpected error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [open, refreshPortalSession]);

  const portrait = sessionData?.portrait?.encodedImage || '';

  if (!open) return null;
  return (
    <Overlay>
      <Modal role="dialog" aria-modal="true">
        <ModalInner>
          <ModalHeader>
            <Title>
              <img src={qrCodeUrl} alt="QR" style={{ width: 20, height: 20 }} /> Your Digital Student ID
            </Title>
            <div>
              <Close aria-label="close" onClick={onClose}>×</Close>
            </div>
          </ModalHeader>

          <ProfileCard>
            <Avatar style={{ backgroundImage: `url(${`data:${portrait.startsWith('/9j/') ? 'image/jpeg' : 'image/png'};base64,${portrait}`})` }} />
            <ProfileInfo>
              <Name>{`${toTitleCaseSingle(sessionData?.givenNames || '')} ${toTitleCaseSingle(sessionData?.surname || '')}`}</Name>
              <Meta>ID: {userId}</Meta>
              <div>
                <BluePill>Junior • Computer Science</BluePill>
              </div>
              <Meta>{emailFromName(sessionData?.givenNames || '', sessionData?.surname || '')}</Meta>
            </ProfileInfo>
          </ProfileCard>
          {!isMobile && (
            <QRBox>
              {loading && <MetaText>Generating QR…</MetaText>}
              {!loading && error && (
                <ErrorText>{error}</ErrorText>
              )}
              {!loading && !error && qrPayload && (
                <div dangerouslySetInnerHTML={{ __html: generateQRCodeSVG(qrPayload, 160) }} />
              )}
              {!loading && !error && qrPayload && (
                <QRHint>Scan with iDen2 app</QRHint>
              )}
            </QRBox>
          )}

          {isMobile && !loading && error && (
            <ErrorText>{error}</ErrorText>
          )}

          {isMobile && (
            loading ? (
              <Primary type="button" disabled>
                Generating…
              </Primary>
            ) : (
              <Primary
                type="button"
                disabled={!qrPayload}
                onClick={() => {
                  if (qrPayload) {
                    window.location.assign(qrPayload);
                  }
                }}
              >
                Add to wallet
              </Primary>
            )
          )}

          <Steps>
            <StepTitle>How to add your student ID on your phone</StepTitle>
            <StepList>
              <StepItem><span>📱</span> 1. Open the iDen2 mobile app</StepItem>
              <StepItem><img src={qrCodeUrl} alt="QR" style={{ width: 18, height: 18 }} /> 2. Scan this QR code with your camera</StepItem>
              <StepItem><span>✅</span> 3. Present your digital ID when requested</StepItem>
            </StepList>
          </Steps>
        </ModalInner>
      </Modal>
    </Overlay>
  );
};

export default StudentIdModal;


